/**
 * AgenticPaymentLedger - Utility Functions
 *
 * Shared formatting helpers used across ledger components.
 */

/**
 * Format a number with locale-aware thousand separators.
 * @param {number|string} val - Value to format
 * @param {number} decimals - Decimal places (default 3)
 * @returns {string}
 */
export const formatNumber = (val, decimals = 3) => {
    if (val == null || isNaN(val)) return "0";
    return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

/**
 * Format a rate value to fixed decimals with a dash fallback.
 * @param {number|string} val
 * @param {number} decimals - Decimal places (default 7)
 * @returns {string}
 */
export const formatRate = (val, decimals = 7) => {
    if (val == null || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return "-";
    return num.toFixed(decimals);
};

/**
 * Format a fee value to fixed decimals with a dash fallback.
 * @param {number|string} val
 * @param {number} decimals - Decimal places (default 3)
 * @returns {string}
 */
export const formatFee = (val, decimals = 3) => {
    if (val == null || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return "-";
    return num.toFixed(decimals);
};

/**
 * The USDb allocation a pool should hold: its circulating supply valued at
 * the sell rate (tokens per USD), the same rule the signing server uses to
 * set a pool up. Null when there is no real rate to value it with: a missing
 * rate, or the 1.0 the signing server falls back to when its rate lookup
 * fails, on a token that is not a dollar.
 * @param {Object} pool - A row from pool metrics
 * @returns {number|null}
 */
export const fairAllocation = (pool) => {
    if (!pool) return null;
    const supply = Number(pool.circulatingSupply);
    const rate = Number(pool.sellRate);
    const isDollar = /USD/i.test(pool.currencySymbol || pool.currencyName || "");
    if (!Number.isFinite(supply) || supply < 0) return null;
    if (isDollar) return Number(supply.toFixed(6));
    if (!Number.isFinite(rate) || rate <= 0 || rate === 1) return null;
    return Number((supply / rate).toFixed(6));
};

const CSV_COLUMNS = [
    ["Token ID", (p) => p.tokenId],
    ["Currency Name", (p) => p.currencyName],
    ["Initial Supply", (p) => p.initialSupply],
    ["Total Supply", (p) => p.totalSupply],
    ["Circulating Supply", (p) => p.circulatingSupply],
    ["USDb Allocation", (p) => p.usdbAllocation],
    ["Fair USDb Allocation", (p) => fairAllocation(p)],
    ["Buy Rate", (p) => p.buyRate],
    ["Sell Rate", (p) => p.sellRate],
    ["Price Rate", (p) => p.priceRate],
    ["Price Pool", (p) => p.pricePool],
    ["Variation", (p) => p.variation],
    ["Base Fee", (p) => p.baseFee],
    ["Dynamic Fee", (p) => p.dynamicFee],
    ["Treasury Balance", (p) => p.treasuryBalance],
    ["Internal Balance", (p) => p.internalBalance],
    ["Chain Synced At", (p) => p.chainSyncedAt],
    ["Stale", (p) => (p.stale == null ? "" : p.stale ? "yes" : "no")],
];

// The columns that mean something for the USDb and USDC reserve rows.
const RESERVE_CSV_COLUMNS = new Set([
    "Token ID", "Currency Name", "Initial Supply", "Total Supply",
    "Circulating Supply",
]);

const csvField = (val) => {
    if (val == null) return "";
    const str = String(val);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

/**
 * The pool rows as CSV, with the table's columns plus the fair allocation.
 * Numbers are left unformatted so a spreadsheet reads them as numbers.
 * @param {Object[]} pools
 * @returns {string}
 */
export const poolsCsv = (pools) =>
    [
        CSV_COLUMNS.map(([label]) => label),
        ...pools.map((p) =>
            CSV_COLUMNS.map(([label, get]) => (p.isReserve && !RESERVE_CSV_COLUMNS.has(label) ? "" : get(p))),
        ),
    ]
        .map((row) => row.map(csvField).join(","))
        .join("\n");

/**
 * Save the given pool rows as ledger_pools_YYYY-MM-DD.csv.
 * @param {Object[]} pools
 */
export const downloadPoolsCsv = (pools) => {
    const blob = new Blob([poolsCsv(pools)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ledger_pools_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};
